<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap align-items-center justify-content-between my-schedule mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <h4 class="font-weight-bold">서비스 에러 로그</h4>
          </div>
          <div class="d-flex align-items-center">
            <button class="btn btn-primary btn-sm mr-2" @click="fetchLogs"><i class="fas fa-sync-alt mr-1"></i> 새로고침</button>
            <span class="text-muted small">마지막 업데이트: {{ lastUpdated }}</span>
          </div>
        </div>
      </div>
    </div>
    <!-- 필터 영역 -->
    <div class="row mb-3">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body py-2">
            <div class="d-flex flex-wrap align-items-center">
              <div class="btn-group mr-3 mb-2">
                <button class="btn btn-sm" :class="dateRange === 'today' ? 'btn-primary' : 'btn-outline-primary'" @click="setDateRange('today')">오늘</button>
                <button class="btn btn-sm" :class="dateRange === 'yesterday' ? 'btn-primary' : 'btn-outline-primary'" @click="setDateRange('yesterday')">어제</button>
                <button class="btn btn-sm" :class="dateRange === 'week' ? 'btn-primary' : 'btn-outline-primary'" @click="setDateRange('week')">최근 7일</button>
                <button class="btn btn-sm" :class="dateRange === 'month' ? 'btn-primary' : 'btn-outline-primary'" @click="setDateRange('month')">최근 30일</button>
              </div>
              <select class="form-control form-control-sm mr-2 mb-2" v-model="filterService" style="width: 180px;">
                <option value="">모든 서비스</option>
                <option v-for="s in availableServices" :key="s" :value="s">{{ s }}</option>
              </select>
              <input type="text" class="form-control form-control-sm mr-2 mb-2" v-model="filterCompanyId" placeholder="Company ID" style="width: 150px;" />
              <button class="btn btn-sm btn-success mb-2" @click="fetchLogs"><i class="fas fa-search"></i> 조회</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 요약 카드 -->
    <div class="row mb-4">
      <div class="col-md-3"><div class="card bg-danger text-white"><div class="card-body text-center py-3"><h6 class="card-title mb-1">총 에러</h6><h4 class="mb-0">{{ stats.totalErrors || 0 }}</h4></div></div></div>
      <div class="col-md-3"><div class="card bg-info text-white"><div class="card-body text-center py-3"><h6 class="card-title mb-1">영향 서비스</h6><h4 class="mb-0">{{ (stats.services || []).length }}</h4></div></div></div>
      <div class="col-md-3"><div class="card bg-warning text-dark"><div class="card-body text-center py-3"><h6 class="card-title mb-1">영향 회사</h6><h4 class="mb-0">{{ (stats.companies || []).length }}</h4></div></div></div>
      <div class="col-md-3"><div class="card bg-secondary text-white"><div class="card-body text-center py-3"><h6 class="card-title mb-1">에러 유형</h6><h4 class="mb-0">{{ (stats.errorTypes || []).length }}</h4></div></div></div>
    </div>
    <!-- 서비스별 통계 -->
    <div class="row mb-4">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header py-2"><h6 class="mb-0">서비스별 에러 현황</h6></div>
          <div class="card-body py-2">
            <div class="d-flex flex-wrap">
              <span v-for="s in serviceStats" :key="s._id" class="badge badge-pill mr-2 mb-1" :class="getServiceBadgeClass(s._id)" style="font-size: 0.9rem;">{{ s._id }}: {{ s.count }}</span>
              <span v-if="serviceStats.length === 0" class="text-muted">에러 없음</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 에러 로그 테이블 -->
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header"><h5 class="mb-0">에러 로그 목록 ({{ pagination.totalCount }}건)</h5></div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-5"><b-spinner variant="primary"></b-spinner><p class="mt-2">로딩 중...</p></div>
            <div v-else-if="logs.length === 0" class="text-center py-5 text-muted"><i class="fas fa-check-circle fa-3x mb-3 text-success"></i><p>조회된 에러 로그가 없습니다.</p></div>
            <CDataTable
                storage-key="ServiceErrorLogs" v-else :headers="headers" :items="logs" itemKey="_id" :items-per-page="50" :show-select="false" :height="'500px'">
              <template v-slot:시간="{ item }"><span class="small">{{ formatTime(item.timestamp) }}</span></template>
              <template v-slot:에러타입="{ item }"><span class="badge" :class="getErrorTypeBadgeClass(item.errorType)">{{ item.errorType }}</span></template>
              <template v-slot:서비스="{ item }"><code class="small">{{ item.service }}.{{ item.functionName }}</code></template>
              <template v-slot:에러메시지="{ item }"><span class="text-danger small" :title="item.errorMessage">{{ truncate(item.errorMessage, 80) }}</span></template>
              <template v-slot:폴백="{ item }"><span v-if="item.fallbackAction" class="text-info small">{{ item.fallbackAction }}</span><span v-else class="text-muted">-</span></template>
            </CDataTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CDataTable from "@/components/common/CDataTable";

export default {
  name: 'ServiceErrorLogs',
  components: { CDataTable },
  data() {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return {
      loading: false, logs: [], stats: {}, serviceStats: [],
      pagination: { page: 1, limit: 50, totalCount: 0, totalPages: 0 },
      lastUpdated: '-', dateRange: 'today', startDate: todayStr, endDate: todayStr,
      filterService: '', filterCompanyId: '',
      availableServices: ['seoService', 'mainImageService', 'detailImageService', 'quotationService', 'koreanLabelService', 'sizeChartService', 'additionalImageService', 'pricingService'],
      headers: [
        { text: '시간', value: '시간', isSlot: true, width: '140px' },
        { text: 'Company', value: 'companyId', isSlot: false, width: '100px' },
        { text: '서비스.함수', value: '서비스', isSlot: true, width: '220px' },
        { text: '에러타입', value: '에러타입', isSlot: true, width: '150px' },
        { text: '에러메시지', value: '에러메시지', isSlot: true },
        { text: '폴백', value: '폴백', isSlot: true, width: '150px' },
        { text: 'ProductId', value: 'productId', isSlot: false, width: '100px' }
      ]
    };
  },
  mounted() { this.fetchLogs(); },
  methods: {
    async fetchLogs() {
      this.loading = true;
      try {
        const params = { startDate: this.startDate, endDate: this.endDate, page: this.pagination.page, limit: this.pagination.limit };
        if (this.filterService) params.service = this.filterService;
        if (this.filterCompanyId) params.companyId = this.filterCompanyId;
        const response = await axios.get('/api/admin/service-errors', { params });
        if (response.data.success) {
          this.logs = response.data.data.logs; this.stats = response.data.data.stats;
          this.serviceStats = response.data.data.serviceStats; this.pagination = response.data.data.pagination;
        }
        this.lastUpdated = new Date().toLocaleTimeString();
      } catch (error) { console.error('에러 로그 조회 실패:', error); }
      finally { this.loading = false; }
    },
    setDateRange(range) {
      this.dateRange = range;
      const today = new Date();
      const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (range === 'today') { this.startDate = formatDate(today); this.endDate = formatDate(today); }
      else if (range === 'yesterday') { const y = new Date(today); y.setDate(y.getDate() - 1); this.startDate = formatDate(y); this.endDate = formatDate(y); }
      else if (range === 'week') { const w = new Date(today); w.setDate(w.getDate() - 7); this.startDate = formatDate(w); this.endDate = formatDate(today); }
      else if (range === 'month') { const m = new Date(today); m.setDate(m.getDate() - 30); this.startDate = formatDate(m); this.endDate = formatDate(today); }
      this.fetchLogs();
    },
    formatTime(ts) { if (!ts) return '-'; const d = new Date(ts); return `${d.getMonth()+1}/${d.getDate()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`; },
    truncate(str, len) { if (!str) return ''; return str.length > len ? str.substring(0, len) + '...' : str; },
    getErrorTypeBadgeClass(t) { const m = { 'S3_UPLOAD_FAILED': 'badge-warning', 'AI_REQUEST_FAILED': 'badge-danger', 'IMAGE_PROCESS_FAILED': 'badge-warning', 'BACKGROUND_REMOVAL_FAILED': 'badge-info', 'TRANSLATION_FAILED': 'badge-primary', 'COUPANG_DOC_UPDATE_FAILED': 'badge-secondary', 'UNKNOWN': 'badge-dark' }; return m[t] || 'badge-secondary'; },
    getServiceBadgeClass(s) { const m = { 'mainImageService': 'badge-primary', 'detailImageService': 'badge-info', 'seoService': 'badge-success', 'quotationService': 'badge-warning', 'koreanLabelService': 'badge-danger', 'sizeChartService': 'badge-secondary' }; return m[s] || 'badge-dark'; }
  }
};
</script>

<style scoped>
.card { box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
code { background-color: #f8f9fa; padding: 2px 6px; border-radius: 4px; color: #e83e8c; }
</style>